import { Adapter } from './adapter';
import { firstValueFrom } from 'rxjs';
import { StockObjectModel, StockPositionModel } from 'src/packages/warehouse';
import { BookingPosition } from '../../types';
import { Log } from '../../util/log';

/**
 * Liest/schreibt StockObject-Bestände im lokalen Bestand.
 */
export class StockObjectAdapter extends Adapter {
  /**
   * Lädt ein StockObject aus dem lokalen Bestand.
   *
   * @param id StockObject-ID
   * @returns Das StockObject oder `null`, wenn es fehlt oder nicht lesbar ist.
   */
  public async findLocal(id: number): Promise<StockObjectModel | null> {
    try {
      const result = await firstValueFrom(
        this.services.stockObjectService.find(id),
      );
      return result ?? null;
    } catch (error) {
      Log.error('StockObjectAdapter.findLocal', error, { id });
      return null;
    }
  }

  /**
   * Schreibt das StockObject mit frischem Zeitstempel in den lokalen Bestand.
   *
   * @param stockObject Zu speicherndes StockObject
   * @returns `false`, wenn das Speichern fehlschlägt.
   */
  public async saveLocalForce(stockObject: StockObjectModel): Promise<boolean> {
    try {
      await this.services.stockObjectService.saveLocalForce(stockObject);
      return true;
    } catch (error) {
      Log.error('StockObjectAdapter.saveLocalForce', error, {
        id: stockObject.id,
      });
      return false;
    }
  }

  /**
   * Lokal gebuchte Menge eines Artikels in einem StockObject.
   *
   * @param stockObjectId StockObject-ID
   * @param articleId Artikel-ID
   * @returns Die Menge, oder 0, wenn Position oder StockObject fehlen.
   */
  public async getArticleAmountLocal(
    stockObjectId: number,
    articleId: number,
  ): Promise<number> {
    const stockObject = await this.findLocal(stockObjectId);
    return (
      stockObject?.positions?.find(p => p.article_id === articleId)?.amount ?? 0
    );
  }

  /**
   * Verändert die lokale Position eines Artikels um `delta` (legt sie bei Bedarf
   * an) und speichert das StockObject.
   *
   * @param position Buchungsposition (liefert die Artikel-ID)
   * @param stockObjectId Ziel-StockObject
   * @param delta Bestandsänderung (negativ = Abgang)
   * @returns `false`, wenn das StockObject fehlt oder das Speichern fehlschlägt.
   */
  public async updatePositionAmountLocal(
    position: BookingPosition,
    stockObjectId: number,
    delta: number,
  ): Promise<boolean> {
    const stockObject = await this.findLocal(stockObjectId);
    if (!stockObject) {
      return false;
    }
    if (!stockObject.positions) {
      stockObject.positions = [];
    }
    const existing = stockObject.positions?.find(
      pos => pos.article_id === position.article_id,
    );
    if (existing) {
      existing.amount += delta;
    } else {
      const article = await this.adapters.article.findLocal(
        position.article_id,
      );
      stockObject.positions.push({
        article_id: position.article_id,
        // Keine Pflichtfelder, werden ggf. beim nächsten Sync nachgezogen
        article_name: article?.name,
        article_number: article?.article_number,
        amount: delta,
      } as StockPositionModel);
    }
    return this.saveLocalForce(stockObject);
  }
}
